import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';
import {
  Box,
  Button,
  ButtonBase,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { FormEvent, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { useRequest } from '../../hooks/useRequest';
import {
  AdminUser,
  createAdminUser,
  fetchAdminUsers,
} from '../../utils/axios/api';

export function AdminUsersPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const loadRequest = useRequest<AdminUser[]>();
  const createRequest = useRequest<{ id: string; token: string }>();
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [createdUserId, setCreatedUserId] = useState<string | null>(null);
  const [createdToken, setCreatedToken] = useState('');
  const [validationError, setValidationError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    void loadRequest.fetch(() => fetchAdminUsers()).then((data) => {
      if (!data) return;
      setUsers(data);
    });
  }, [loadRequest]);

  const resetCreateDialog = useCallback(() => {
    setEmail('');
    setCreatedUserId(null);
    setCreatedToken('');
    setValidationError(null);
    setCopied(false);
  }, []);

  const handleOpenCreateDialog = useCallback(() => {
    resetCreateDialog();
    setIsCreateDialogOpen(true);
  }, [resetCreateDialog]);

  const handleCloseCreateDialog = useCallback(() => {
    const userId = createdUserId;

    setIsCreateDialogOpen(false);
    resetCreateDialog();

    if (userId) {
      navigate(`/admin/users/${userId}`);
    }
  }, [createdUserId, navigate, resetCreateDialog]);

  const handleCreateUser = useCallback(
    async (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();

      const trimmedEmail = email.trim();

      if (!trimmedEmail) {
        setValidationError(t('admin.users.create.emailRequired'));
        return;
      }

      setValidationError(null);
      setCopied(false);

      const created = await createRequest.fetch(() =>
        createAdminUser(trimmedEmail),
      );
      if (!created) {
        return;
      }
      setCreatedUserId(created.id);
      setCreatedToken(created.token);
    },
    [createRequest, email, t],
  );

  const handleCopyToken = useCallback(async () => {
    if (!createdToken) {
      return;
    }

    try {
      await navigator.clipboard.writeText(createdToken);
      setCopied(true);
    } catch {
      setValidationError(t('admin.users.errors.copyFailed'));
    }
  }, [createdToken, t]);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 1.25,
        p: 2,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1.5,
        }}
      >
        <Typography variant="subtitle1">{t('admin.users.description')}</Typography>
        <Button variant="contained" onClick={handleOpenCreateDialog}>
          {t('admin.users.create.button')}
        </Button>
      </Box>
      {loadRequest.isLoading ? (
        <CircularProgress size={24} />
      ) : loadRequest.exception && users.length === 0 ? (
        <Typography variant="body2" color="error">
          {t(`exceptions.${loadRequest.exception}`)}
        </Typography>
      ) : users.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          {t('admin.users.empty')}
        </Typography>
      ) : (
        users.map((user) => (
          <ButtonBase
            key={user.id}
            component={Link}
            to={`/admin/users/${user.id}`}
            sx={{
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              border: 1,
              borderColor: 'divider',
              borderRadius: '10px',
              px: 1.5,
              py: 1.25,
              bgcolor: 'background.paper',
              color: 'inherit',
              textDecoration: 'none',
              textAlign: 'left',
              '&:hover': {
                bgcolor: 'action.hover',
                borderColor: 'text.secondary',
              },
            }}
          >
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography variant="body1" noWrap>
                {user.email}
              </Typography>
              <Typography variant="body2" color="text.secondary" noWrap>
                {user.tools.length > 0
                  ? user.tools.map((tool) => tool.name).join(', ')
                  : t('admin.users.noTools')}
              </Typography>
            </Box>
            <Chip
              size="small"
              label={t(`admin.users.role.${user.role}`)}
              color={user.role === 'ADMIN' ? 'primary' : 'default'}
              variant={user.role === 'ADMIN' ? 'filled' : 'outlined'}
            />
            <Chip
              size="small"
              label={
                user.contentfulPatToken
                  ? t('admin.users.hasPat')
                  : t('admin.users.noPat')
              }
              color={user.contentfulPatToken ? 'success' : 'default'}
              variant={user.contentfulPatToken ? 'filled' : 'outlined'}
            />
          </ButtonBase>
        ))
      )}

      <Dialog
        open={isCreateDialogOpen}
        onClose={handleCloseCreateDialog}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>{t('admin.users.create.title')}</DialogTitle>
        <Box component="form" onSubmit={handleCreateUser}>
          <DialogContent
            sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}
          >
            <TextField
              type="email"
              label={t('admin.users.create.email')}
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              disabled={createRequest.isLoading || Boolean(createdUserId)}
              autoFocus
              required
              fullWidth
            />
            {createdToken ? (
              <>
                <Typography variant="body2" color="text.secondary">
                  {t('admin.users.create.tokenHint')}
                </Typography>
                <TextField
                  value={createdToken}
                  slotProps={{
                    input: {
                      readOnly: true,
                      endAdornment: (
                        <Tooltip
                          title={
                            copied
                              ? t('admin.users.authToken.copied')
                              : t('admin.users.authToken.copy')
                          }
                        >
                          <span>
                            <IconButton
                              aria-label={t('admin.users.authToken.copy')}
                              onClick={() => {
                                void handleCopyToken();
                              }}
                            >
                              <ContentCopyOutlinedIcon />
                            </IconButton>
                          </span>
                        </Tooltip>
                      ),
                    },
                  }}
                  fullWidth
                />
              </>
            ) : null}
            {validationError ?? createRequest.exception ? (
              <Typography variant="body2" color="error">
                {validationError ??
                  t(`exceptions.${createRequest.exception}`)}
              </Typography>
            ) : null}
          </DialogContent>
          <DialogActions>
            {createdUserId ? (
              <Button onClick={handleCloseCreateDialog}>
                {t('admin.users.create.close')}
              </Button>
            ) : (
              <>
                <Button
                  onClick={handleCloseCreateDialog}
                  disabled={createRequest.isLoading}
                >
                  {t('admin.users.create.cancel')}
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={createRequest.isLoading}
                >                  {t('admin.users.create.submit')}
                </Button>
              </>
            )}
          </DialogActions>
        </Box>
      </Dialog>
    </Box>
  );
}
